import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ReportSelectionOption } from '~/shared/utils/types';

import { getApiFilterFromSelectOption } from '../../common/helpers';
import { getTableDateRange, parseSortField } from '../helpers';

const OPTIMIZATION_TABLE_SORT_KEY = 'sort_field';

interface UseOptimizationTableParamsArgs {
  activeDateFilter: string | null;
  apiFilters: string | null;
  dataLimit: number;
  pageOffset: string;
}

export function useOptimizationTableParams({
  activeDateFilter,
  apiFilters,
  dataLimit,
  pageOffset,
}: UseOptimizationTableParamsArgs) {
  const [searchParams] = useSearchParams();

  const sortField = useMemo(
    () => parseSortField(searchParams.get(OPTIMIZATION_TABLE_SORT_KEY)),
    [searchParams]
  );

  const currentReportFilters = useMemo(
    () => JSON.parse(apiFilters || '[]') as ReportSelectionOption[],
    [apiFilters]
  );

  const [tableStartDate, tableEndDate] = useMemo(
    () => getTableDateRange(activeDateFilter),
    [activeDateFilter]
  );

  const parsedFilters = useMemo(() => {
    const processedFilters = searchParams.get('processed_filters');
    if (processedFilters) {
      return JSON.parse(processedFilters) as string[];
    }
    const result = getApiFilterFromSelectOption(currentReportFilters);
    return Array.isArray(result) ? result : [result];
  }, [searchParams, currentReportFilters]);

  const optimizationTableBaseParams: [string, string][] = useMemo(
    () => [
      ['start_date', tableStartDate],
      ['end_date', tableEndDate],
      ...parsedFilters.map((f) => ['filters', f] as [string, string]),
    ],
    [tableStartDate, tableEndDate, parsedFilters]
  );

  const optimizationTableQueryString = useMemo(
    () =>
      new URLSearchParams([
        ...optimizationTableBaseParams,
        [
          'sortBy',
          JSON.stringify({
            field: sortField.field,
            ascending: sortField.dir === 'asc',
          }),
        ],
        ['limit', dataLimit.toString()],
        ['offset', pageOffset],
      ]).toString(),
    [optimizationTableBaseParams, sortField, pageOffset, dataLimit]
  );

  const optimizationTableCountQueryString = useMemo(
    () => new URLSearchParams(optimizationTableBaseParams).toString(),
    [optimizationTableBaseParams]
  );

  const filtersSignature = useMemo(
    () => (parsedFilters.length ? parsedFilters.slice().sort().join('|') : 'none'),
    [parsedFilters]
  );

  const timeSignature = useMemo(
    () => `${tableStartDate}|${tableEndDate}`,
    [tableStartDate, tableEndDate]
  );

  return {
    sortField,
    currentReportFilters,
    parsedFilters,
    optimizationTableQueryString,
    optimizationTableCountQueryString,
    filtersSignature,
    timeSignature,
  };
}
